import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  acquireDocumentScrollLock,
  onAnimationEnd,
  releaseDocumentScrollLock,
  removeWillChangeOnAnimationEnd,
} from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #transitionId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('resize', this.#onResize);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('resize', this.#onResize);

    if (this.isOpen) {
      this.#finishMainClose({ restoreFocus: false });
    } else {
      releaseDocumentScrollLock(this.refs.details);
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /** Close the drawer before its mobile host disappears at a wider breakpoint. */
  #onResize = () => {
    if (!this.isOpen) return;

    const host = this.closest('.header__drawer');
    if (host instanceof HTMLElement && getComputedStyle(host).display === 'none') {
      this.#finishMainClose({ restoreFocus: false });
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;
    const transitionId = ++this.#transitionId;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.refs.menuDrawer.scrollTop = 0;
      this.#lockPageScroll();
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (transitionId !== this.#transitionId) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (
            transitionId !== this.#transitionId ||
            !details.hasAttribute('open') ||
            !details.classList.contains('menu-open')
          ) return;

          trapFocus(drawer || details);
          const closeButton = drawer?.querySelector('.menu-drawer__close-button');
          if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;
    const transitionId = ++this.#transitionId;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (transitionId !== this.#transitionId) return;
        if (details === this.refs.details) {
          this.#finishMainClose({ restoreFocus: true });
        } else {
          reset(details);
          trapFocus(this.refs.menuDrawer);
          summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Keep the document at its exact position while the mobile drawer scrolls.
   * `overflow: hidden` on the root alone is unreliable in mobile Safari.
   */
  #lockPageScroll() {
    acquireDocumentScrollLock(this.refs.details);
  }

  /** Restore document styles and the exact pre-drawer scroll position. */
  #unlockPageScroll() {
    releaseDocumentScrollLock(this.refs.details);
  }

  /**
   * Reset every drawer state owned by this component. Used after the close
   * animation as well as during breakpoint changes and Theme Editor rerenders.
   *
   * @param {{ restoreFocus?: boolean }} options
   */
  #finishMainClose({ restoreFocus = false } = {}) {
    const { details, menuDrawer } = this.refs;
    const summary = details.querySelector('summary');
    this.#transitionId += 1;

    menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
    reset(details);

    removeTrapFocus();
    this.#unlockPageScroll();

    if (restoreFocus && summary instanceof HTMLElement && summary.getClientRects().length) {
      summary.focus({ preventScroll: true });
    }
  }

}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

